// src/pages/Achievements.js
import React, { useState, useEffect, useMemo } from 'react';
import Layout from '../Layout/Layout';
import api from '../utils/apiClient';
import styled, { keyframes, css } from 'styled-components';
import LinkedInShareButton from '../components/LinkedInShareButton';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { DynamicIcon } from '../utils/iconRenderer';
// Animations
const scanline = keyframes`
  0% {
    transform: translateY(-100%);
  }
  100% {
    transform: translateY(100%);
  }
`;

const glowEffect = keyframes`
  0% {
    box-shadow: 0 0 5px rgba(10, 255, 112, 0.5);
  }
  50% {
    box-shadow: 0 0 20px rgba(10, 255, 112, 0.8);
  }
  100% {
    box-shadow: 0 0 5px rgba(10, 255, 112, 0.5);
  }
`;


const typewriterEffect = keyframes`
  from { width: 0 }
  to { width: 100% }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing[16]} 0;

  @media (max-width: 768px) {
    padding: ${({ theme }) => theme.spacing[8]} 0;
  }
`;

const LoadingSpinner = styled.div`
  width: 60px;
  height: 60px;
  border: 3px solid ${({ theme }) => theme.colors.backgroundLighter};
  border-top: 3px solid ${({ theme }) => theme.colors.primary};
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: ${({ theme }) => theme.spacing[6]};
  box-shadow: 0 0 15px ${({ theme }) => theme.colors.glow};
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: -10px;
    left: -10px;
    right: -10px;
    bottom: -10px;
    border: 1px solid ${({ theme }) => theme.colors.primary}33;
    border-radius: 50%;
    animation: ${props => css`${glowEffect} 2s infinite`};
  }
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
  
  @media (max-width: 768px) {
    width: 50px;
    height: 50px;
  }
`;

const LoadingText = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.fonts.sizes.md};
  letter-spacing: 1px;
  position: relative;
  display: inline-block;
  overflow: hidden;
  animation: ${props => css`${typewriterEffect} 2s steps(25, end) infinite alternate`};
  white-space: nowrap;
  
  &::after {
    content: '';
    position: absolute;
    width: 6px;
    height: 6px;
    background-color: ${({ theme }) => theme.colors.primary};
    border-radius: 50%;
    animation: dots 1.5s infinite;
    display: inline-block;
    margin-left: 4px;
  }
  
  @keyframes dots {
    0%, 20% { opacity: 0; }
    50% { opacity: 1; }
    80%, 100% { opacity: 0; }
  }
`;

const NoAchievements = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing[12]};
  background: ${({ theme }) => theme.colors.backgroundLight};
  border-radius: ${({ theme }) => theme.borderRadius['2xl']};
  box-shadow: ${({ theme }) => theme.shadows.strong};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.fonts.sizes.lg};
  position: relative;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.colors.divider};
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 2px;
    background: ${({ theme }) => theme.colors.primary};
    animation: ${props => css`${scanline} 3s linear infinite`};
    opacity: 0.5;
  }
  
  @media (max-width: 768px) {
    padding: ${({ theme }) => theme.spacing[8]};
    font-size: ${({ theme }) => theme.fonts.sizes.md};
  }
`;

const NoResultsButton = styled.button`
  margin-top: ${({ theme }) => theme.spacing[4]};
  padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[4]}`};
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.backgroundDark};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.fonts.sizes.md};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.normal};
  
  &:hover {
    background: ${({ theme }) => theme.colors.primaryDark};
    transform: translateY(-2px);
  }
`;
const AchievementsGrid = styled.section`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); /* Slightly narrower cards */
  gap: 16px; /* Reduced from likely 24px */
  margin-top: 20px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    gap: 12px;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

// Compact page layout
const AchievementsContainer = styled.main`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px; /* Reduced padding */
  
  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const PageHeader = styled.section`
  margin-bottom: 20px; /* Reduced from likely 32px */
  text-align: center;
`;

const Title = styled.h1`
  font-size: 24px; /* Smaller title */
  font-weight: 700;
  margin-bottom: 8px; /* Reduced spacing */
  background: linear-gradient(90deg, #0AFF70, #00BFFF);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  letter-spacing: 1px;
  text-transform: uppercase;
`;

const Subtitle = styled.p`
  color: #A0A0A0;
  font-size: 14px; /* Smaller subtitle */
  max-width: 600px;
  margin: 0 auto;
`;

// More compact filter section
const FilterSection = styled.section`
  margin-bottom: 16px; /* Reduced margin */
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px; /* Reduced gap */
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const SearchBar = styled.div`
  position: relative;
  flex: 1;
  max-width: 300px; /* Slightly narrower */

  @media (max-width: 768px) {
    max-width: 100%;
    width: 100%;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 8px 8px 8px 32px; /* Reduced padding */
  background: #1A1E24;
  border: 1px solid #2A2E33;
  border-radius: 6px;
  color: #FFFFFF;
  font-size: 13px; /* Smaller font */
  
  &:focus {
    outline: none;
    border-color: #0AFF70;
    box-shadow: 0 0 0 1px #0AFF7033;
  }
  
  &::placeholder {
    color: #A0A0A0;
    opacity: 0.6;
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #A0A0A0;
  font-size: 14px; /* Smaller icon */
`;

const FilterGroup = styled.div`
  display: flex;
  gap: 8px; /* Reduced gap */
  align-items: center;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: space-between;
  }
`;

const FilterButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px; /* Reduced padding */
  background: ${({ active }) => active ? '#0AFF70' : '#1A1E24'};
  color: ${({ active }) => active ? '#000000' : '#A0A0A0'};
  border: 1px solid ${({ active }) => active ? '#0AFF70' : '#2A2E33'};
  border-radius: 6px;
  font-size: 12px; /* Smaller font */
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${({ active }) => active ? '#09E065' : '#20252C'};
    border-color: ${({ active }) => active ? '#09E065' : '#0AFF70'};
  }
`;

// Stats section with reduced height
const StatsSection = styled.section`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px; /* Reduced gap */
  margin-bottom: 20px; /* Reduced margin */
  
  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 10px;
    margin-bottom: 16px;
  }
`;

const StatCard = styled.div`
  background: #1A1E24;
  border-radius: 8px;
  padding: 12px; /* Reduced padding */
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  border: 1px solid #2A2E33;
`;

const StatValue = styled.div`
  font-size: 22px; /* Smaller font */
  font-weight: 700;
  color: #0AFF70;
  margin-bottom: 4px; /* Reduced margin */
`;

const StatLabel = styled.div`
  color: #A0A0A0;
  font-size: 12px; /* Smaller font */
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

// Pagination with reduced height
const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 6px;
  margin-top: 20px; /* Reduced margin */
  
  @media (max-width: 768px) {
    margin-top: 16px;
  }
`;

const PaginationButton = styled.button`
  padding: 6px 10px; /* Reduced padding */
  background: ${({ active }) => active ? '#0AFF70' : '#1A1E24'};
  color: ${({ active }) => active ? '#000000' : '#A0A0A0'};
  border: 1px solid ${({ active }) => active ? '#0AFF70' : '#2A2E33'};
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 12px; /* Smaller font */
  
  &:hover:not(:disabled) {
    background: ${({ active }) => active ? '#09E065' : '#20252C'};
    border-color: ${({ active }) => active ? '#09E065' : '#0AFF70'};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const AchievementDetails = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: ${({ theme }) => theme.spacing[4]};
  padding-top: ${({ theme }) => theme.spacing[4]};
  border-top: 1px solid ${({ theme }) => theme.colors.divider};
  position: relative;

  @media (max-width: 480px) {
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[3]};
    align-items: flex-start;
  }
`;

const AchievementStatus = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => `${theme.spacing[1]} ${theme.spacing[3]}`};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-size: ${({ theme }) => theme.fonts.sizes.sm};
  font-weight: 500;
  
  ${({ earned, theme }) => earned ? `
    background: linear-gradient(to right, rgba(10, 255, 112, 0.13), rgba(10, 255, 112, 0.07));
    color: ${theme.colors.primary};
    border: 1px solid rgba(10, 255, 112, 0.2);
    box-shadow: 0 0 10px rgba(10, 255, 112, 0.2);
  ` : `
    background-color: rgba(255, 255, 255, 0.05);
    color: ${theme.colors.textSecondary};
    border: 1px solid ${theme.colors.divider};
  `}

  @media (max-width: 480px) {
    font-size: ${({ theme }) => theme.fonts.sizes.xs};
    padding: ${({ theme }) => `${theme.spacing[1]} ${theme.spacing[2]}`};
  }
`;

const AchievementCard = styled(motion.div)`
  background: #1A1E24;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  padding: 16px; /* Reduced from likely 24px or more */
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;
  border: 1px solid #2A2E33;
  height: 180px; /* Fixed height to make all cards uniform */
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  
  ${({ earned, theme }) => earned ? `
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 2px;
      background: ${theme.colors.gradientPrimary || 'linear-gradient(90deg, #0AFF70, #00BFFF)'};
    }
  ` : `
    filter: grayscale(0.8);
    opacity: 0.8;
  `}

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 6px 14px rgba(0, 0, 0, 0.3);
  }
`;

const AchievementHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px; /* Reduced spacing */
  margin-bottom: 10px; /* Reduced from likely 16px */
`;

const AchievementIcon = styled.div`
  font-size: 24px;
  background: ${({ earned }) => earned ?
    `radial-gradient(circle, rgba(10, 255, 112, 0.1) 0%, rgba(10, 255, 112, 0) 70%)` :
    `radial-gradient(circle, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0) 70%)`
  };
  padding: 12px;
  border-radius: 8px;
  filter: ${({ earned }) => earned ? 'drop-shadow(0 0 8px rgba(10, 255, 112, 0.5))' : 'none'};
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  flex-shrink: 0;
  
  img {
    width: 32px;
    height: 32px;
    object-fit: contain;
    border-radius: 4px;
  }
  
  svg {
    width: 24px;
    height: 24px;
  }
`;

const AchievementInfo = styled.div`
  flex: 1;
  overflow: hidden; /* Prevent text overflow */
`;

const AchievementTitle = styled.h3`
  font-size: 18px; /* Smaller title */
  font-weight: 600;
  margin: 0 0 4px; /* Reduced margin */
  color: ${({ earned }) => earned ? '#FFFFFF' : '#A0A0A0'};
  ${({ earned }) => earned && `text-shadow: 0 0 10px rgba(10, 255, 112, 0.13);`}
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const AchievementDescription = styled.p`
  color: #A0A0A0;
  font-size: 14px; /* Smaller description text */
  margin: 0;
  line-height: 1.3; /* Tighter line height */
  display: -webkit-box;
  -webkit-line-clamp: 2; /* Limit to 2 lines */
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const XpBadge = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 4px 8px; /* Reduced padding */
  background: linear-gradient(90deg, rgba(10, 12, 16, 0.95) 0%, rgba(15, 25, 18, 0.97) 100%);
  color: #0AFF70;
  border-radius: 4px;
  font-weight: 600;
  font-size: 13px; /* Smaller text */
  box-shadow: 0 0 10px rgba(10, 255, 112, 0.25);
  border: 1px solid rgba(10, 255, 112, 0.3);
  
  &::before {
    content: "+";
    margin-right: 2px;
    font-weight: bold;
  }
`;


const TypeTag = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  padding: 3px 6px; /* Smaller tag */
  border-radius: 4px;
  font-size: 10px; /* Smaller text */
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: ${({ type }) => {
    if (type === 'course') return 'rgba(0, 123, 255, 0.1)';
    if (type === 'quiz') return 'rgba(180, 180, 180, 0.1)';
    if (type === 'streak') return 'rgba(180, 180, 180, 0.1)';
    return 'rgba(255, 255, 255, 0.05)';
  }};
  color: ${({ type }) => {
    if (type === 'course') return '#007BFF';
    if (type === 'quiz') return '#B4B4B4';
    if (type === 'streak') return '#B4B4B4';
    return '#FFFFFF';
  }};
  border: 1px solid ${({ type }) => {
    if (type === 'course') return 'rgba(0, 123, 255, 0.2)';
    if (type === 'quiz') return 'rgba(180, 180, 180, 0.2)';
    if (type === 'streak') return 'rgba(180, 180, 180, 0.2)';
    return 'rgba(255, 255, 255, 0.1)';
  }};
`;
// Progress indicator
const ProgressContainer = styled.div`
  margin-top: ${({ theme }) => theme.spacing[3]};
  width: 100%;
`;

const ProgressLabel = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: ${({ theme }) => theme.fonts.sizes.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: ${({ theme }) => theme.spacing[1]};
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 5px;
  background-color: ${({ theme }) => theme.colors.backgroundLighter};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  width: ${({ progress }) => progress}%;
  background: ${({ theme }) => theme.colors.gradientPrimary};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  transition: width 0.5s ease;
`;



// Custom hook for pagination
const usePagination = (items, itemsPerPage) => {
  const [currentPage, setCurrentPage] = useState(1);
  const maxPage = Math.ceil(items.length / itemsPerPage);

  const currentItems = items.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const next = () => {
    setCurrentPage((prev) => Math.min(prev + 1, maxPage));
  };

  const prev = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const jump = (page) => {
    const pageNumber = Math.max(1, Math.min(page, maxPage));
    setCurrentPage(pageNumber);
  };

  return {
    next,
    prev,
    jump,
    currentItems,
    currentPage,
    maxPage,
  };
};

const Achievements = () => {
  const [achievements, setAchievements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [showEarned, setShowEarned] = useState(true);
  const [showLocked, setShowLocked] = useState(true);
  const [sortBy, setSortBy] = useState('newest');

  // Stats calculations
  const totalAchievements = achievements.length;
  const earnedAchievements = achievements.filter(a => a.earned).length;
  const totalXP = achievements.reduce((sum, a) => a.earned ? sum + a.xpReward : sum, 0);
  const completionPercentage = totalAchievements > 0
    ? Math.round((earnedAchievements / totalAchievements) * 100)
    : 0;

  useEffect(() => {
    const fetchAchievements = async () => {
      try {
        const response = await api.achievements.getAll();
        setAchievements(response);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching achievements:', error);
        setError('Failed to load achievements. Please try again later.');
        setLoading(false);
      }
    };

    fetchAchievements();
  }, []);

  // Filter and sort achievements
  const filteredAchievements = useMemo(() => {
    return achievements
      .filter((achievement) => {
        // Filter by search term
        const matchesSearch =
          achievement.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          achievement.description.toLowerCase().includes(searchTerm.toLowerCase());

        // Filter by type
        const matchesType =
          filterType === 'all' ||
          achievement.achievement_type === filterType;

        // Filter by earned/locked status
        const matchesStatus =
          (showEarned && achievement.earned) ||
          (showLocked && !achievement.earned);

        return matchesSearch && matchesType && matchesStatus;
      })
      .sort((a, b) => {
        // Sort by different criteria
        if (sortBy === 'newest') {
          return b.id - a.id; // Assuming higher ID = newer
        } else if (sortBy === 'oldest') {
          return a.id - b.id;
        } else if (sortBy === 'xp-high') {
          return b.xpReward - a.xpReward;
        } else if (sortBy === 'xp-low') {
          return a.xpReward - b.xpReward;
        } else if (sortBy === 'earned-first') {
          return a.earned ? -1 : 1;
        } else if (sortBy === 'locked-first') {
          return a.earned ? 1 : -1;
        }
        return 0;
      });
  }, [achievements, searchTerm, filterType, showEarned, showLocked, sortBy]);

  // Pagination
  const ITEMS_PER_PAGE = 9;
  const {
    next,
    prev,
    jump,
    currentItems: paginatedAchievements,
    currentPage,
    maxPage,
  } = usePagination(filteredAchievements, ITEMS_PER_PAGE);

  // Reset pagination when filters change
  useEffect(() => {
    jump(1);
  }, [searchTerm, filterType, showEarned, showLocked, sortBy, jump]);

  // Helper function to get a readable achievement type label
  const getAchievementTypeLabel = (type) => {
    switch (type) {
      case 'course_completion':
        return 'COURSE';
      case 'module_completion':
        return 'MODULE';
      case 'challenge_completion':
        return 'CHALLENGE';
      case 'skill_mastery':
        return 'SKILL';
      default:
        return type.toUpperCase().replace('_', ' ');
    }
  };

  // Track when a user shares an achievement
  const handleShare = async (achievement, platform) => {
    try {
      // Call the API to track share analytics
      await api.achievements.trackShare(achievement.id, platform);

      // Show success message
      // Achievement shared successfully
    } catch (error) {
      // Silently fail - analytics shouldn't interrupt user experience
      console.error('Error tracking share:', error);
    }
  };

  // Handle resetting filters
  const resetFilters = () => {
    setSearchTerm('');
    setFilterType('all');
    setShowEarned(true);
    setShowLocked(true);
    setSortBy('newest');
  };

  // Card animations
  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
    exit: { opacity: 0, y: -20, transition: { duration: 0.2 } }
  };
  const { currentUser } = useAuth();
  // Get available achievement types for the filter
  const achievementTypes = useMemo(() => {
    return ['all', ...new Set(achievements.map(a => a.achievement_type))];
  }, [achievements]);

  return (
    <Layout>
      <AchievementsContainer>
        <PageHeader>
          <Title>Achievements</Title>
          <Subtitle>Track your cyber security accomplishments</Subtitle>
        </PageHeader>

        {loading ? (
          <LoadingContainer>
            <LoadingSpinner />
            <LoadingText>Loading achievements</LoadingText>
          </LoadingContainer>
        ) : error ? (
          <NoAchievements>
            <p>{error}</p>
          </NoAchievements>
        ) : achievements.length > 0 ? (
              <>
                {/* Stats Section */}
                <StatsSection>
                  <StatCard>
                    <StatValue>{earnedAchievements}</StatValue>
                    <StatLabel>Achievements Earned</StatLabel>
                  </StatCard>
                  <StatCard>
                    <StatValue>{totalAchievements - earnedAchievements}</StatValue>
                    <StatLabel>Achievements Locked</StatLabel>
                  </StatCard>
                  <StatCard>
                    <StatValue>{totalXP}</StatValue>
                    <StatLabel>Total XP Earned</StatLabel>
                  </StatCard>
                  <StatCard>
                    <StatValue>{completionPercentage}%</StatValue>
                    <StatLabel>Completion</StatLabel>
                    <ProgressContainer>
                      <ProgressBar>
                        <ProgressFill progress={completionPercentage} />
                      </ProgressBar>
                    </ProgressContainer>
                  </StatCard>
                </StatsSection>

                {/* Filter Section */}
                <FilterSection>
                  <SearchBar>
                    <SearchIcon><DynamicIcon iconName="fa-search" style={{ fontSize: '14px' }} /></SearchIcon>
                    <SearchInput
                      type="text"
                      placeholder="Search achievements..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </SearchBar>

                  <FilterGroup>
                    <FilterButton
                      as="select"
                      value={filterType}
                      onChange={(e) => setFilterType(e.target.value)}
                    >
                      {achievementTypes.map(type => (
                        <option key={type} value={type}>
                          {type === 'all' ? 'All Types' : getAchievementTypeLabel(type)}
                        </option>
                      ))}
                    </FilterButton>

                    <FilterButton
                      active={showEarned}
                      onClick={() => setShowEarned(!showEarned)}
                    >
                      <DynamicIcon iconName="fa-trophy" style={{ fontSize: '14px' }} /> Earned
                    </FilterButton>

                    <FilterButton
                      active={showLocked}
                      onClick={() => setShowLocked(!showLocked)}
                    >
                      <DynamicIcon iconName="fa-lock" style={{ fontSize: '14px' }} /> Locked
                    </FilterButton>

                    <FilterButton
                      as="select"
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                    >
                      <option value="newest">Newest First</option>
                      <option value="oldest">Oldest First</option>
                      <option value="xp-high">Highest XP</option>
                      <option value="xp-low">Lowest XP</option>
                      <option value="earned-first">Earned First</option>
                      <option value="locked-first">Locked First</option>
                    </FilterButton>
                  </FilterGroup>
                </FilterSection>

                {/* Achievements Grid */}
                {filteredAchievements.length > 0 ? (
                  <>
                    <AnimatePresence>
                      <AchievementsGrid>
                        {paginatedAchievements.map((achievement) => (
                          <AchievementCard
                            key={achievement.id}
                            earned={achievement.earned}
                            initial="hidden"
                            animate="visible"
                            exit="exit"
                            variants={cardVariants}
                            layout
                          >
                            <TypeTag type={achievement.achievement_type}>
                              {getAchievementTypeLabel(achievement.achievement_type)}
                            </TypeTag>

                            <AchievementHeader>
                              <AchievementIcon earned={achievement.earned}>
                                {achievement.earned && achievement.icon_image ? (
                                  <img
                                    src={achievement.icon_image.startsWith('http') ? achievement.icon_image : `http://localhost:8000${achievement.icon_image}`}
                                    alt={achievement.title}
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      e.target.nextSibling.style.display = 'block';
                                    }}
                                  />
                                ) : null}

                                {/* Dynamic FontAwesome icon or fallback */}
                                <div style={{
                                  display: achievement.earned && achievement.icon_image ? 'none' : 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '24px',
                                  width: '100%',
                                  height: '100%'
                                }}>
                                  {achievement.earned ? (
                                    achievement.icon ? (
                                      <DynamicIcon 
                                        iconName={achievement.icon} 
                                        style={{ fontSize: '24px', color: 'inherit' }}
                                      />
                                    ) : (
                                      '🏆'
                                    )
                                  ) : (
                                    <DynamicIcon 
                                      iconName="fa-lock" 
                                      style={{ fontSize: '24px', color: 'inherit' }}
                                    />
                                  )}
                                </div>
                              </AchievementIcon>

                              <AchievementInfo>
                                <AchievementTitle earned={achievement.earned}>
                                  {achievement.title}
                                </AchievementTitle>
                                <AchievementDescription>
                                  {achievement.description}
                                </AchievementDescription>
                              </AchievementInfo>
                            </AchievementHeader>

                            <AchievementDetails>
                              <AchievementStatus earned={achievement.earned}>
                                {achievement.earned ? 'EARNED' : 'LOCKED'}
                              </AchievementStatus>

                              {/* <AchievementActions>
                                <AchievementReward earned={achievement.earned}>
                                  {achievement.xpReward} XP
                                </AchievementReward>

                                {achievement.earned && (
                                  <ShareButton
                                    achievement={achievement}
                                    onShare={(platform) => handleShare(achievement, platform)}
                                  />
                                )}
                              </AchievementActions> */}
                              <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                                <XpBadge>{achievement.xpReward} XP</XpBadge>

                                {achievement.earned && (
                                  <LinkedInShareButton achievement={achievement}
                                    userId={currentUser.id} verification_id={achievement.verification_id}>
                                    <DynamicIcon iconName="fa-share" style={{ fontSize: '14px' }} /> Share Badge
                                  </LinkedInShareButton>
                                )}
                              </div>
                            </AchievementDetails>

                            {!achievement.earned && achievement.progress > 0 && (
                              <ProgressContainer>
                                <ProgressLabel>
                                  <span>Progress</span>
                                  <span>{achievement.progress}%</span>
                                </ProgressLabel>
                                <ProgressBar>
                                  <ProgressFill progress={achievement.progress} />
                                </ProgressBar>
                              </ProgressContainer>
                            )}
                          </AchievementCard>
                        ))}
                      </AchievementsGrid>
                    </AnimatePresence>

                    {/* Pagination */}
                    {maxPage > 1 && (
                      <PaginationContainer>
                        <PaginationButton onClick={prev} disabled={currentPage === 1}>
                          Previous
                        </PaginationButton>

                        {Array.from({ length: maxPage }, (_, i) => i + 1).map((page) => (
                          <PaginationButton
                            key={page}
                            active={currentPage === page}
                            onClick={() => jump(page)}
                          >
                            {page}
                          </PaginationButton>
                        ))}

                        <PaginationButton onClick={next} disabled={currentPage === maxPage}>
                          Next
                        </PaginationButton>
                      </PaginationContainer>
                    )}
                  </>
                ) : (
                  <NoAchievements>
                    <p>No achievements found matching your filters.</p>
                    <NoResultsButton onClick={resetFilters}>
                      Reset Filters
                    </NoResultsButton>
                  </NoAchievements>
                )}
              </>
        ) : (
          <NoAchievements>
            <p>No achievements available yet. Start exploring courses to earn achievements!</p>
          </NoAchievements>
        )}
      </AchievementsContainer>
    </Layout>
  );
};

export default Achievements;